/**
 * spaceHazardStep.ts
 * The hazard-pool phase of the World 2 space-scene fixed step.
 *
 * Extracted verbatim from `spaceStep.ts` so that module stays under the
 * AGENTS.md 300-line cap. This file owns one cohesive slice of the tick:
 * roll every hazard spawner, integrate the active pools, and resolve the
 * ship-projectile / ship-missile collisions against them — asteroids,
 * enemies (Poisson + wave-script), the boss and its weapons, and the
 * Level 2-3 homing mines.
 *
 * Called once per tick from `stepSpace`, positioned exactly where the
 * inline block used to sit, so the ordering guarantees the DECISION
 * comments below depend on are preserved. Operates on module-level
 * `spaceState`; no allocation, no async — same hot-path contract as
 * `stepSpace`.
 *
 * NO React imports, NO Reanimated imports, NO async.
 *
 * @module spaceHazardStep
 */

import { spaceState } from './spaceState';
import { resolveShipMissileCollisions } from './missileCollisions';
import {
  updateAsteroidSpawner,
  updateAsteroids,
  resolveShipProjectileAsteroidCollisions,
} from './asteroidSystem';
import {
  updateSpaceEnemySpawner,
  updateSpaceEnemies,
  resolveShipProjectileEnemyCollisions,
} from './spaceEnemySystem';
import { updateWaveScheduler } from './waveScheduler';
import {
  triggerBoss,
  updateBoss,
  updateBossProjectiles,
  updateBossMissiles,
  resolveShipProjectileBossCollisions,
} from './bossSystem';
import {
  updateHomingMineSpawner,
  updateHomingMines,
} from './homingMineSpawner';

/**
 * Run the hazard-pool phase for one fixed tick.
 *
 * @param dt - Fixed timestep in seconds (always FIXED_STEP).
 * @param spawnGate - When false, all fresh spawners are suppressed (level
 *   complete, end-condition crossing this tick, or outro grace ticking);
 *   in-flight pools still integrate so on-screen hazards drift off
 *   naturally instead of freezing.
 */
export function stepSpaceHazards(dt: number, spawnGate: boolean): void {
  const cfg = spaceState.levelConfig;

  // ── 6–7. Asteroids ──────────────────────────────────────────────────────────
  // Spawn → integrate → collide. Spawning before update means a brand-new
  // asteroid drifts one tick on the spawn frame, matching the projectile
  // pipeline so first-frame placement is identical to subsequent frames.
  // Collision runs LAST so projectile and asteroid positions are both
  // current-tick, avoiding visible one-frame-late kills.
  //
  // DECISION: Only the SPAWNER is gated by `levelConfig.asteroidsEnabled`.
  // `updateAsteroids` and the collision pass always run so any asteroid
  // left over from a prior phase (e.g. mid-level config swap, or boss
  // arrival in a future level) drifts off-screen instead of freezing.
  //
  // DECISION: Spawner is ALSO gated on `!levelComplete` so the post-clear
  // fade-out / score-tally phase doesn't push fresh asteroids into the
  // viewport behind the Sector Cleared overlay. In-flight asteroids keep
  // integrating so anything already on screen drifts off naturally instead
  // of freezing.
  if (cfg.asteroidsEnabled && spawnGate) {
    updateAsteroidSpawner(dt);
  }
  updateAsteroids(dt);
  resolveShipProjectileAsteroidCollisions();

  // ── 8–9. Enemies ───────────────────────────────────────────────────────────
  // Same shape as asteroids: spawn → integrate → collide. Runs AFTER asteroid
  // collision so a ship projectile that already hit an asteroid this tick
  // (and is now `dead`) can't double-dip on an enemy in the same frame.
  // Spawner gated per-level AND on !levelComplete (same fade-out reason as
  // asteroids); update + collide always run so a leftover enemy never
  // freezes mid-flight.
  if (cfg.enemiesEnabled && spawnGate) {
    updateSpaceEnemySpawner(dt);
  }
  // DECISION: Wave scheduler runs alongside the Poisson spawner (a level
  // can use either, both, or neither). Both spawn paths funnel into the
  // shared `SPAWN_SPEC` pools so the pool-cap protects against runaway
  // enemy counts regardless of which path produced them. Gated on the
  // same `spawnGate` so the post-clear fade-out can't trigger fresh
  // waves behind the Sector Cleared overlay.
  if (cfg.waveScript !== undefined && spawnGate) {
    updateWaveScheduler(dt, cfg.waveScript);
  }
  updateSpaceEnemies(dt);
  resolveShipProjectileEnemyCollisions();

  // ── 9b. Boss ───────────────────────────────────────────────────────────────
  // Same shape: trigger → integrate → collide. Trigger runs before update so
  // a freshly-spawned boss gets a tick of motion on the spawn frame, matching
  // the projectile / asteroid / enemy pipelines. `updateBossProjectiles` and
  // `updateBossMissiles` tick the in-flight turret bolts and homing missiles;
  // both run AFTER `updateBoss` so a weapon fired on this tick gets its first
  // dt of travel on the same frame (matching the ship-projectile pipeline).
  // Collision runs LAST so a projectile that already hit an enemy this tick
  // can't double-dip on the boss in the same frame.
  // Trigger gated per-level AND on !levelComplete so an already-completed
  // duration level can't summon a boss during the fade-out. Update + collide
  // always run so a spawned boss can finish its defeat sequence.
  if (cfg.bossEnabled && spawnGate) {
    triggerBoss();
  }
  updateBoss(dt);
  updateBossProjectiles(dt);
  updateBossMissiles(dt);
  resolveShipProjectileBossCollisions();

  // ── 9b·5. Ship missile cross-pool collisions ──────────────────────────────
  // Runs after every projectile collision pass so an enemy/asteroid/boss
  // already killed by a laser this tick can't be double-counted by the
  // missile's direct hit. Splash radius reads the current-tick positions
  // of every live hazard pool in one go.
  resolveShipMissileCollisions();

  // ── 9c. Homing mines (Level 2-3 only) ──────────────────────────────────────
  // Spawner is gated on `id === '2-3'` AND on `spawnGate` so the post-clear
  // fade-out can't push fresh mines into the viewport behind the Sector
  // Cleared overlay. `updateHomingMines` runs UNCONDITIONALLY so its
  // internal level-transition watcher fires when the scene re-inits onto a
  // non-2-3 level — that path releases any leftover mines back to the pool
  // without a dedicated SpaceScene hook. See ROADMAP.md line 221.
  if (cfg.id === '2-3' && spawnGate) {
    updateHomingMineSpawner(dt);
  }
  updateHomingMines(dt);
}
